import {
  buildErrorReportProperties,
  buildIdentifyProperties,
  toError,
  type ErrorReportContext,
} from '@hit-app/convex/analytics';
import { usePostHog } from '@posthog/react';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useWorkOSAuth } from './auth';

const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

const isPostHogConfigured = Boolean(posthogToken);

if (import.meta.env.DEV && !isPostHogConfigured) {
  console.warn('PostHog: VITE_POSTHOG_PROJECT_TOKEN nicht gesetzt. Analytics deaktiviert.');
}

export const posthogClient = posthog;

const posthogOptions = {
  api_host: posthogHost || 'https://eu.i.posthog.com',
  defaults: '2026-01-30',
  disabled: !isPostHogConfigured,
} as const;

posthogClient.init(posthogToken ?? 'placeholder_key', posthogOptions);

type PostHogErrorContext = Omit<ErrorReportContext, 'screen'>;

export function reportPostHogError(error: unknown, context: PostHogErrorContext) {
  if (!isPostHogConfigured) {
    return;
  }
  posthogClient.captureException(toError(error), buildErrorReportProperties('web', context));
}

export function PostHogAuthBridge() {
  const posthogInstance = usePostHog();
  const { user, organizationId, isLoading } = useWorkOSAuth();
  const lastIdentifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (user) {
      posthogInstance?.identify(user.id, buildIdentifyProperties(user, organizationId));

      if (organizationId) {
        posthogInstance?.group('organization', organizationId, {
          organization_id: organizationId,
        });
      }

      lastIdentifiedUserIdRef.current = user.id;
      return;
    }

    if (lastIdentifiedUserIdRef.current) {
      posthogInstance?.reset();
      lastIdentifiedUserIdRef.current = null;
    }
  }, [isLoading, organizationId, posthogInstance, user]);

  return null;
}
